import moment from 'moment';
import { Logger } from 'common/logger';
import { maxBy } from 'lodash';

export const WEATHER_DEFAULT_STATE = {
    component: {
        isActive: true,
    },
    data: {
        id: 'WEATHER',
        entityType: 'WEATHER',
        key: { value: 'WEATHER' },
        last_update_time: undefined,
        tide_height: { unit: 'm', value: undefined },
        ts: { value: undefined },
        water_temperature: { value: undefined },
        wave_height: { unit: 'm', value: undefined },
        wind_direction: { unit: 'deg', value: undefined },
        wind_speed: { unit: 'm/s', value: undefined },
        ambient_temperature: { value: undefined },
    },
    historical: {
        wind: { data: [], metadata: { id: 'wind' } },
        windDirection: { data: [], metadata: { id: 'windDirection' } },
        wave: { data: [], metadata: { id: 'wave' } },
        tide: { data: [], metadata: { id: 'tide' } },
        bottomAxis: { data: [], metadata: { id: 'bottomAxis' } },
        topAxis: { data: [], metadata: { id: 'topAxis' } },
    },
    forecast: {
        wind: { data: [], metadata: { id: 'wind' } },
        windDirection: { data: [], metadata: { id: 'windDirection' } },
        wave: { data: [], metadata: { id: 'wave' } },
        tide: { data: [], metadata: { id: 'tide' } },
        bottomAxis: { data: [], metadata: { id: 'bottomAxis' } },
        topAxis: { data: [], metadata: { id: 'topAxis' } },
    },
    temperature: {
        water: { unit: '', times: [], values: [] },
        air: { unit: '', times: [], values: [] },
    },
    lightningRisk: [],
    polling: null,
};

const deepCopy = (obj) => JSON.parse(JSON.stringify(obj));

export const transformHistorical = (weatherData, historicalData) => {
    const data = deepCopy(WEATHER_DEFAULT_STATE.historical);
    let actualTimestamp;
    if (weatherData.ts && weatherData.ts.value) {
        actualTimestamp = weatherData.ts.value;
    } else {
        Logger.info('Weather data is missing TS value (actual values not added):', weatherData);
    }

    const dataCount = {
        forecast: 10,
        historical: 40,
    };

    function getHistoricalValues(d) {
        if (!d || !d.values) {
            Logger.debug('No historical data');
            return [];
        }
        // historical data is reversed
        return d.values
            .map((dB, dI) => ({ timestamp: d.times[dI], value: dB }))
            .slice(0, dataCount.historical)
            .reverse();
    }

    let h = historicalData;

    if (h) {
        h = h.values;
        data.tide.data = getHistoricalValues(h.tide_height);
        data.wave.data = getHistoricalValues(h.wave_height);
        data.wind.data = getHistoricalValues(h.wind_speed);
        data.windDirection.data = getHistoricalValues(h.wind_direction);
        data.bottomAxis.data = getHistoricalValues(h.wind_speed);
        data.topAxis.data = getHistoricalValues(h.wind_speed);
        data.tide.metadata = { unit: weatherData.tide_height.unit, maxValue: maxBy(data.tide.data, 'value')?.value };
        data.wave.metadata = { unit: weatherData.wave_height.unit, maxValue: maxBy(data.wave.data, 'value')?.value };
        data.wind.metadata = { unit: weatherData.wind_speed.unit, maxValue: maxBy(data.wind.data, 'value')?.value };
        data.windDirection.metadata = { unit: weatherData.wind_direction.unit };
    }

    function pushActualValues(d, timestamp, key) {
        if (!d || !timestamp) {
            return;
        }
        if (data[key].data) {
            data[key].data.push({ timestamp, value: d.value });
        }
    }

    pushActualValues(weatherData.tide_height, actualTimestamp, 'tide');
    pushActualValues(weatherData.wave_height, actualTimestamp, 'wave');
    pushActualValues(weatherData.wind_speed, actualTimestamp, 'wind');
    pushActualValues(weatherData.wind_direction, actualTimestamp, 'windDirection');
    pushActualValues(weatherData.wind_speed, actualTimestamp, 'bottomAxis');
    pushActualValues(weatherData.wind_speed, actualTimestamp, 'topAxis');

    return data;
};

const dataCountDefault = {
    forecast: 10,
    historical: 40,
};

export const transformForecast = (weatherData, forecastData, dataCount = dataCountDefault) => {
    const data = deepCopy(WEATHER_DEFAULT_STATE.historical);

    let actualTimestamp = new Date().toISOString();
    if (weatherData.ts && weatherData.ts.value) {
        actualTimestamp = weatherData.ts.value;
    } else {
        Logger.info('Using new Date as actualTimestamp ', actualTimestamp);
    }

    function normalizeDate(d) {
        return new Date(moment(d).utc()).getTime();
    }

    function getForecastValues(d) {
        // filter only future timestamps
        // trim the array to 10 to match tide span
        // (tide_height: 300 points, 6 minutes interval, 30 heures)
        // vs (wind_speed, 30 points, 3 hours interval, 7.5 days)
        return d.value
            .filter((dB) => normalizeDate(dB.tm) > normalizeDate(actualTimestamp))
            .map((dB) => ({ timestamp: dB.tm, value: dB.value }))
            .slice(0, dataCount.forecast);
    }

    function getTideForecastValues(d, timestamps) {
        // filter timestamps to be in same span as others
        return d.value
            .filter(
                (dB) =>
                    normalizeDate(dB.tm) <= normalizeDate(timestamps[timestamps.length - 1]) &&
                    normalizeDate(dB.tm) >= normalizeDate(timestamps[0])
            )
            .map((dB) => ({ timestamp: dB.tm, value: dB.value }));
    }

    const f = forecastData;
    if (f) {
        const windSpeedForecast = getForecastValues(f.wind_speed);
        data.wave.data = getForecastValues(f.wave_height);
        data.wind.data = windSpeedForecast;
        data.windDirection.data = getForecastValues(f.wind_direction);
        data.bottomAxis.data = windSpeedForecast;
        data.topAxis.data = windSpeedForecast;
        data.tide.data = getTideForecastValues(
            f.tide_height,
            windSpeedForecast.map((d) => d.timestamp)
        );

        data.tide.metadata = { unit: weatherData.tide_height?.unit, maxValue: maxBy(data.tide.data, 'value')?.value };
        data.wave.metadata = { unit: weatherData.wave_height?.unit, maxValue: maxBy(data.wave.data, 'value')?.value };
        data.wind.metadata = { unit: weatherData.wind_speed?.unit, maxValue: maxBy(data.wind.data, 'value')?.value };
        data.windDirection.metadata = { unit: weatherData.wind_direction?.unit };
    }

    return data;
};

export const transformLightningRisk = (payload) => {
    const values = payload.lightning_risk?.value || [];

    const currDate = moment().utc();

    const filtered = values.filter((item) =>
        moment(item.tm)
            .utc()
            .isAfter(currDate)
    );

    const maxItems = 8;
    return filtered.length > maxItems ? filtered.slice(0, maxItems) : filtered;
};
